/**
 * Space Task Recurrence
 *
 * Parses recurrence rules and calculates the next task occurrence dates.
 *
 * Primary responsibilities:
 * - Normalize task recurrence JSON into a typed rule
 * - Calculate next start/due dates for completion and schedule triggers
 *
 * Dependencies:
 * - Recurring task shared types
 */

import type { Json } from "@/supabase/types"
import type {
  TaskRecurrenceEnd,
  TaskRecurrenceFrequency,
  TaskRecurrenceMode,
  TaskRecurrenceRule,
  TaskRecurrenceTrigger,
  TaskRecurrenceWriteFields
} from "@/types/recurring"

const dayMs = 24 * 60 * 60 * 1000
const maxScanDays = 370
const frequencyLabels: Record<TaskRecurrenceFrequency, string> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  yearly: "year"
}
const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

type DateSource = {
  startDate?: string | null
  dueDate?: string | null
  recurrenceNextAt?: string | null
}

type RecurringTaskLike = DateSource & {
  recurrence_completed_count?: number | null
}

type DatePair = {
  startDate: string | null
  dueDate: string | null
  nextAt: string | null
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

const getString = (value: unknown) => {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null
}

const getPositiveInteger = (value: unknown, fallback: number) => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN

  if (!Number.isInteger(parsed) || parsed < 1) {
    return fallback
  }

  return Math.min(parsed, 999)
}

const normalizeFrequency = (
  value: unknown,
  fallback: TaskRecurrenceFrequency = "weekly"
): TaskRecurrenceFrequency => {
  const normalized =
    typeof value === "string"
      ? value.trim().toLowerCase().replace(/[-\s]+/g, "_")
      : ""

  if (normalized === "daily" || normalized === "day" || normalized === "days") {
    return "daily"
  }

  if (
    normalized === "weekly" ||
    normalized === "week" ||
    normalized === "weeks"
  ) {
    return "weekly"
  }

  if (
    normalized === "monthly" ||
    normalized === "month" ||
    normalized === "months"
  ) {
    return "monthly"
  }

  if (
    normalized === "yearly" ||
    normalized === "annual" ||
    normalized === "annually" ||
    normalized === "year" ||
    normalized === "years"
  ) {
    return "yearly"
  }

  return fallback
}

const isTrigger = (value: unknown): value is TaskRecurrenceTrigger => {
  return value === "on_completion" || value === "on_schedule"
}

const isMode = (value: unknown): value is TaskRecurrenceMode => {
  return value === "create_new" || value === "reschedule"
}

const getDate = (value: string | null | undefined) => {
  if (!value) {
    return null
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const toIso = (date: Date | null) => {
  return date ? date.toISOString() : null
}

const cloneDate = (date: Date) => {
  return new Date(date.getTime())
}

const getMonthEndDay = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate()
}

const addMonthsClamped = (date: Date, months: number, dayOfMonth?: number | null) => {
  const next = cloneDate(date)
  const targetDay = dayOfMonth ?? next.getDate()
  next.setDate(1)
  next.setMonth(next.getMonth() + months)
  next.setDate(Math.min(targetDay, getMonthEndDay(next.getFullYear(), next.getMonth())))
  return next
}

const addInterval = (date: Date, rule: TaskRecurrenceRule) => {
  const next = cloneDate(date)

  if (rule.frequency === "daily") {
    next.setDate(next.getDate() + rule.interval)
    return next
  }

  if (rule.frequency === "weekly") {
    next.setDate(next.getDate() + rule.interval * 7)
    return alignToSelectedWeekday(next, rule.daysOfWeek)
  }

  if (rule.frequency === "monthly") {
    return addMonthsClamped(next, rule.interval, rule.dayOfMonth)
  }

  next.setFullYear(next.getFullYear() + rule.interval)
  return next
}

const alignToSelectedWeekday = (date: Date, daysOfWeek?: number[]) => {
  if (!daysOfWeek?.length) {
    return date
  }

  const selectedDays = new Set(daysOfWeek)
  const candidate = cloneDate(date)

  for (let offset = 0; offset < 7; offset += 1) {
    if (selectedDays.has(candidate.getDay())) {
      return candidate
    }
    candidate.setDate(candidate.getDate() + 1)
  }

  return date
}

const shiftWeekend = (date: Date) => {
  const next = cloneDate(date)
  const day = next.getDay()

  if (day === 6) {
    next.setDate(next.getDate() + 2)
  } else if (day === 0) {
    next.setDate(next.getDate() + 1)
  }

  return next
}

const getNextOccurrenceDate = (
  anchorDate: Date,
  rule: TaskRecurrenceRule,
  afterDate?: Date
) => {
  let candidate = addInterval(anchorDate, rule)
  const threshold = afterDate ?? anchorDate
  let scannedDays = 0

  while (candidate.getTime() <= threshold.getTime() && scannedDays < maxScanDays) {
    candidate = addInterval(candidate, rule)
    scannedDays += rule.frequency === "daily" ? rule.interval : 7
  }

  return rule.skipWeekends ? shiftWeekend(candidate) : candidate
}

const getAnchorDate = (
  source: DateSource,
  fallbackDate: Date
) => {
  return (
    getDate(source.dueDate) ||
    getDate(source.startDate) ||
    getDate(source.recurrenceNextAt) ||
    fallbackDate
  )
}

const buildShiftedDatePair = (
  source: DateSource,
  nextAnchorDate: Date
): DatePair => {
  const sourceDueDate = getDate(source.dueDate)
  const sourceStartDate = getDate(source.startDate)

  if (sourceDueDate && sourceStartDate) {
    const durationMs = sourceDueDate.getTime() - sourceStartDate.getTime()
    return {
      dueDate: toIso(nextAnchorDate),
      nextAt: toIso(nextAnchorDate),
      startDate: toIso(new Date(nextAnchorDate.getTime() - durationMs))
    }
  }

  if (sourceDueDate) {
    return {
      dueDate: toIso(nextAnchorDate),
      nextAt: toIso(nextAnchorDate),
      startDate: null
    }
  }

  if (sourceStartDate) {
    return {
      dueDate: null,
      nextAt: toIso(nextAnchorDate),
      startDate: toIso(nextAnchorDate)
    }
  }

  return {
    dueDate: toIso(nextAnchorDate),
    nextAt: toIso(nextAnchorDate),
    startDate: null
  }
}

const parseEnd = (value: unknown): TaskRecurrenceEnd => {
  if (!isRecord(value)) {
    return { type: "never" }
  }

  if (value.type === "after_occurrences") {
    return {
      type: "after_occurrences",
      count: getPositiveInteger(value.count, 1)
    }
  }

  if (value.type === "on_date") {
    const date = getString(value.date)
    return date ? { type: "on_date", date } : { type: "never" }
  }

  return { type: "never" }
}

const normalizeDaysOfWeek = (value: unknown) => {
  if (!Array.isArray(value)) {
    return undefined
  }

  const days = Array.from(
    new Set(
      value
        .map(day => (typeof day === "number" ? day : Number(day)))
        .filter(day => Number.isInteger(day) && day >= 0 && day <= 6)
    )
  ).sort((left, right) => left - right)

  return days.length > 0 ? days : undefined
}

export const parseTaskRecurrenceRule = (
  value: unknown
): TaskRecurrenceRule | null => {
  if (!isRecord(value) || value.enabled !== true) {
    return null
  }

  return {
    enabled: true,
    frequency: normalizeFrequency(value.frequency),
    interval: getPositiveInteger(value.interval, 1),
    trigger: isTrigger(value.trigger) ? value.trigger : "on_completion",
    mode: isMode(value.mode) ? value.mode : "create_new",
    daysOfWeek: normalizeDaysOfWeek(value.daysOfWeek),
    dayOfMonth:
      value.dayOfMonth === null || value.dayOfMonth === undefined
        ? null
        : Math.min(31, getPositiveInteger(value.dayOfMonth, 1)),
    skipWeekends: value.skipWeekends === true,
    end: parseEnd(value.end),
    timezone: getString(value.timezone)
  }
}

export const createDefaultTaskRecurrenceRule = (
  source?: DateSource
): TaskRecurrenceRule => {
  const anchorDate = getAnchorDate(source ?? {}, new Date())

  return {
    enabled: true,
    frequency: "weekly",
    interval: 1,
    trigger: "on_completion",
    mode: "create_new",
    daysOfWeek: [anchorDate.getDay()],
    dayOfMonth: anchorDate.getDate(),
    skipWeekends: false,
    end: { type: "never" },
    timezone:
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null
  }
}

export const taskRecurrenceRuleToJson = (
  rule: TaskRecurrenceRule | null
): Json | null => {
  return rule ? (rule as unknown as Json) : null
}

export const getTaskRecurrenceNextDatePair = (
  source: DateSource,
  rule: TaskRecurrenceRule,
  referenceDate: Date = new Date()
): DatePair => {
  const anchorDate = getAnchorDate(source, referenceDate)
  const nextAnchorDate = getNextOccurrenceDate(anchorDate, rule, referenceDate)
  return buildShiftedDatePair(source, nextAnchorDate)
}

export const getScheduledTaskOccurrenceDatePair = (
  source: DateSource,
  rule: TaskRecurrenceRule,
  referenceDate: Date = new Date()
): DatePair & { followingNextAt: string | null } => {
  const currentAnchorDate =
    getDate(source.recurrenceNextAt) || getAnchorDate(source, referenceDate)
  const currentPair = buildShiftedDatePair(source, currentAnchorDate)
  const followingAnchorDate = getNextOccurrenceDate(
    currentAnchorDate,
    rule,
    currentAnchorDate
  )

  return {
    ...currentPair,
    followingNextAt: toIso(followingAnchorDate)
  }
}

export const buildTaskRecurrenceWriteFields = (
  rule: TaskRecurrenceRule | null,
  source: DateSource
): TaskRecurrenceWriteFields => {
  if (!rule) {
    return {
      recurrence_rule: null,
      recurrence_next_at: null
    }
  }

  return {
    recurrence_rule: taskRecurrenceRuleToJson(rule),
    recurrence_next_at: getTaskRecurrenceNextDatePair(
      source,
      rule,
      new Date()
    ).nextAt
  }
}

export const hasTaskRecurrenceEnded = (
  task: RecurringTaskLike,
  rule: TaskRecurrenceRule,
  nextAt: string | null,
  completedIncrement = 0
) => {
  const end = rule.end ?? { type: "never" }

  if (end.type === "never") {
    return false
  }

  if (end.type === "on_date") {
    const endDate = getDate(end.date)
    const nextDate = getDate(nextAt)
    return Boolean(endDate && nextDate && nextDate.getTime() > endDate.getTime())
  }

  const completedCount = Math.max(
    0,
    (task.recurrence_completed_count ?? 0) + completedIncrement
  )
  return completedCount >= end.count
}

export const getTaskRecurrenceSummary = (rule: TaskRecurrenceRule | null) => {
  if (!rule) {
    return "Does not repeat"
  }

  const unit = frequencyLabels[rule.frequency]
  const cadence =
    rule.interval === 1
      ? `Every ${unit}`
      : `Every ${rule.interval} ${unit}s`
  const weeklyDays =
    rule.frequency === "weekly" && rule.daysOfWeek?.length
      ? ` on ${rule.daysOfWeek.map(day => weekdayLabels[day]).join(", ")}`
      : ""
  const trigger =
    rule.trigger === "on_completion" ? "after completion" : "on schedule"
  const mode =
    rule.mode === "create_new" ? "creates a new task" : "reschedules this task"

  return `${cadence}${weeklyDays}, ${trigger}, ${mode}`
}
